// src/pages/agent/Settings.js
// Agent profile + admin-only "Manage Agents" panel (laketia/admin sees this).
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addDoc, collection, doc, getDoc, getDocs, onSnapshot, query, setDoc, updateDoc, serverTimestamp, Timestamp, deleteDoc, writeBatch } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase';
import { useAgentAuth } from '../../lib/auth';
import { PageHeader, Card, Btn, Field, Input, Pill, BORDER, MID, LIGHT, BURG_DK, BURG, GOLD, CREAM } from '../../components/agent/UI';
import { getNotifyPrefs, setNotifyPrefs, playChime, ensureBrowserNotifyPermission } from '../../lib/notify';
import { INTRO_SMS_OPTIONS } from '../../lib/smsTemplates';
import { registerForPush, unregisterFromPush, isPushRegistered } from '../../lib/push';

export default function Settings() {
  const { user, profile } = useAgentAuth();
  const isAdmin = profile?.role === 'admin';

  return (
    <>
      <PageHeader
        eyebrow="Settings"
        title="Your profile"
        sub="Update your agent profile. Brokerage and license fields appear on your generated contracts."
      />

      <ProfileCard profile={profile} user={user} />
      <ShareLinkCard profile={profile} />
      <IntroSmsCard />
      {profile?.role !== 'lead_submitter' && <NotificationsCard profile={profile} />}

      {isAdmin && (
        <div id="admin">
          <PageHeader
            eyebrow="Admin"
            title="Manage agents"
            sub="Invite new agents, view active accounts, revoke access."
          />
          <InviteCard adminUid={profile.uid} adminEmail={profile.email} />
          <AddLeadAgentCard adminUid={profile.uid} />
          <AgentList adminEmail={profile.email} />

          <PageHeader
            eyebrow="Business profile"
            title="Company defaults"
            sub="Names that go on every contract regardless of which agent sends it. Set once — applies to everyone."
          />
          <BusinessProfileCard />

          <PageHeader
            eyebrow="Automation"
            title="Daily outreach batch"
            sub="Sends the intro text at 9:13 AM ET every day to a batch of JV-tagged leads. Bad numbers roll to the next-in-line on the next run."
          />
          <OutreachSettingsCard profile={profile} />

          <PageHeader
            eyebrow="Sharing"
            title="JV partner share link"
            sub="Password-gated public page your JV partner can open to see current outreach status without a HomeIQa login."
          />
          <JvShareCard profile={profile} />

          <PageHeader
            eyebrow="Import"
            title="Bulk lead import"
            sub="Paste a Google Sheet URL or a CSV export. Handles PropStream / BatchLeads / DealMachine column layouts out of the box."
          />
          <ImportLeadsCard profile={profile} />

          <SeedSampleLeadCard />
          <MigrateMailboxesCard />
          <SyncQuoMessagesCard />
          <SyncQuoInboundCard />
          <OptOutAuditCard />
          <ModerationLogCard />

          <PageHeader
            eyebrow="Connections"
            title="Integrations"
            sub="Status of every external service HomeIQa relies on. Test the connection from here."
          />
          <QuoConnectionCard />
        </div>
      )}
    </>
  );
}

function ProfileCard({ profile, user }) {
  const [form, setForm] = useState({
    name: profile?.name || '',
    contactEmail: profile?.contactEmail || '',
    phone: profile?.phone || '',
    brokerage: profile?.brokerage || '',
    licenseNumber: profile?.licenseNumber || ''
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true); setSaved(false);
    try {
      await updateDoc(doc(db, 'agents', profile.uid), { ...form, updatedAt: serverTimestamp() });
      setSaved(true);
    } finally { setSaving(false); }
  };

  return (
    <Card title="Agent profile">
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 18 }}>
        {user?.photoURL && <img src={user.photoURL} alt="" style={{ width: 56, height: 56, borderRadius: '50%', border: `2px solid ${GOLD}` }} referrerPolicy="no-referrer" />}
        <div>
          <div style={{ fontSize: 14, color: BURG_DK }}>{profile?.contactEmail || user?.email}</div>
          <Pill color={profile?.role === 'admin' ? 'gold' : 'green'}>{profile?.role || 'agent'}</Pill>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label="Display name"><Input value={form.name} onChange={e => set('name', e.target.value)} /></Field>
        <Field label="Contact email" hint="Shown in the portal instead of your login email. Login still uses your Google account(s) either way."><Input value={form.contactEmail} onChange={e => set('contactEmail', e.target.value)} placeholder={user?.email} /></Field>
        <Field label="Phone"><Input value={form.phone} onChange={e => set('phone', e.target.value)} /></Field>
        <Field label="Brokerage / Company"><Input value={form.brokerage} onChange={e => set('brokerage', e.target.value)} /></Field>
        <Field label="License #"><Input value={form.licenseNumber} onChange={e => set('licenseNumber', e.target.value)} /></Field>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, alignItems: 'center' }}>
        {saved && <span style={{ fontSize: 12, color: '#0F6B3F' }}>✓ Saved</span>}
        <Btn onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save profile'}</Btn>
      </div>
    </Card>
  );
}

function BusinessProfileCard() {
  const DEFAULTS = {
    legalEntityName: 'Towanjalin L. Jones',
    defaultBuyerName: 'Towanjalin L. Jones and/or assigns',
    defaultAifName: 'Towanjalin L. Jones',
    buyerAddress: ''
  };
  const [form, setForm] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'settings', 'company'));
        if (snap.exists()) {
          setForm({ ...DEFAULTS, ...snap.data() });
        }
      } catch {} finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, []);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true); setSaved(false);
    try {
      await setDoc(doc(db, 'settings', 'company'), {
        legalEntityName: form.legalEntityName.trim(),
        defaultBuyerName: form.defaultBuyerName.trim(),
        defaultAifName: form.defaultAifName.trim(),
        buyerAddress: (form.buyerAddress || '').trim(),
        updatedAt: serverTimestamp()
      }, { merge: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Card title="Business profile"><div style={{ color: LIGHT, padding: 20 }}>Loading…</div></Card>;

  return (
    <Card title="Contract defaults" right={<Pill color="gold">Admin only</Pill>}>
      <div style={{ fontSize: 13, color: MID, lineHeight: 1.65, marginBottom: 16 }}>
        These names auto-fill on every contract any agent generates — no matter who sends it.
        All deals go out under the LLC; Denicia, future agents, and you all use the same defaults.
      </div>

      <Field label="Legal entity name" hint="Your legal signing name — currently Towanjalin L. Jones. Used as the principal in the defaults below.">
        <Input value={form.legalEntityName} onChange={e => setField('legalEntityName', e.target.value)} placeholder="Towanjalin L. Jones" />
      </Field>

      <Field label="Default Buyer name" hint="Goes on PSA, Cancellation, and as the Assignor on Assignment contracts.">
        <Input value={form.defaultBuyerName} onChange={e => setField('defaultBuyerName', e.target.value)} placeholder="Towanjalin L. Jones and/or assigns" />
      </Field>

      <Field label="Default Attorney-in-Fact" hint='On the Authorization to Sign Listing Documents (POA). Common pattern: "[Your name], as Member/Manager of [LLC]".'>
        <Input value={form.defaultAifName} onChange={e => setField('defaultAifName', e.target.value)} placeholder="Towanjalin L. Jones" />
      </Field>

      <Field label="Business mailing address (optional)" hint="Used as the Assignor address on Assignment contracts when applicable.">
        <Input value={form.buyerAddress} onChange={e => setField('buyerAddress', e.target.value)} placeholder="123 Main St, Atlanta, GA 30303" />
      </Field>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, alignItems: 'center', marginTop: 6 }}>
        {saved && <span style={{ fontSize: 12, color: '#0F6B3F' }}>✓ Saved · all agents now see these defaults</span>}
        <Btn onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save defaults'}</Btn>
      </div>
    </Card>
  );
}

// Pull any inbound SMS conversations from Quo whose phone we don't
// already have on a lead, and create a new lead for each. Runs
// automatically every 24 hours via the scheduledSyncQuoInbound
// Cloud Function — this card lets the admin trigger it on demand.
function SyncQuoInboundCard() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const sync = async () => {
    setBusy(true); setResult(null);
    try {
      const fn = httpsCallable(functions, 'syncQuoInbound');
      const res = await fn({});
      setResult(res.data);
    } catch (e) {
      setResult({ ok: false, message: e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Pull new leads from Quo inbound" right={<Pill color="gold">Admin only</Pill>}>
      <div style={{ fontSize: 13, color: MID, lineHeight: 1.65, marginBottom: 14 }}>
        Walks every recent Quo conversation. For each one with a phone we don't already have on a
        lead, creates a new lead (status: <em>New</em>, source: <em>inbound_sms</em>, needsReview).
        Webhook handles real-time inbound automatically; this is the catch-all that runs every 24
        hours OR whenever you click below.
      </div>
      <Btn variant="gold" onClick={sync} disabled={busy}>
        {busy ? 'Syncing…' : '📨 Pull new leads now'}
      </Btn>
      {result && (
        <div style={{ marginTop: 14, padding: 12, background: result.ok ? '#E7F5EE' : '#FEF2F2', border: `1px solid ${result.ok ? 'rgba(15,107,63,0.3)' : '#FCA5A5'}`, borderRadius: 8, fontSize: 13, color: result.ok ? '#0F6B3F' : '#991B1B' }}>
          {result.ok ? (
            <>
              <strong>✓ Synced.</strong> Checked {result.conversationsChecked} conversation{result.conversationsChecked === 1 ? '' : 's'};
              created {result.leadsCreated} new lead{result.leadsCreated === 1 ? '' : 's'};
              {result.alreadyKnown} already known.
              {result.errors?.length > 0 && (
                <div style={{ marginTop: 8, fontSize: 11, color: MID }}>
                  ⚠ {result.errors.length} error{result.errors.length === 1 ? '' : 's'} (first: {result.errors[0].error}).
                </div>
              )}
            </>
          ) : <strong>⚠ {result.message}</strong>}
        </div>
      )}
    </Card>
  );
}

// SMS opt-out audit. Every number that texted STOP / UNSUBSCRIBE
// shows here. Admin can also clear (re-add) an opt-out with a
// confirmation note when the seller verbally consents.
function OptOutAuditCard() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(null); // entry being cleared

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { collection: col, getDocs: gd, query: q, orderBy: ob, limit: lim } = await import('firebase/firestore');
        const snap = await gd(q(col(db, 'optedOutNumbers'), ob('optedOutAt', 'desc'), lim(50)));
        if (!cancelled) setEntries(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.warn('optedOutNumbers read failed:', e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const reAdd = async (entry, confirmation) => {
    try {
      const fn = httpsCallable(functions, 'clearOptOut');
      await fn({ phone: entry.phone, confirmation });
      setEntries(es => es.map(x => x.id === entry.id ? { ...x, reAddedAt: { toDate: () => new Date() }, reAddedConfirmation: confirmation } : x));
      setClearing(null);
    } catch (e) {
      alert(e.message); // rare; would surface as inline error in production
    }
  };

  return (
    <Card title="SMS opt-outs" right={<Pill color="red">Audit · Admin only</Pill>}>
      <div style={{ fontSize: 13, color: MID, lineHeight: 1.65, marginBottom: 14 }}>
        Every seller who's texted STOP / UNSUBSCRIBE / CANCEL etc. lives here. Outbound SMS to these
        numbers is blocked at HomeIQa AND at the carrier level. If a seller has verbally said they
        want to be contacted again, you can clear the opt-out below.
      </div>
      {loading ? (
        <div style={{ fontSize: 13, color: LIGHT, padding: '14px 0' }}>Loading…</div>
      ) : entries.length === 0 ? (
        <div style={{ fontSize: 13, color: '#0F6B3F', padding: '14px 16px', background: '#E7F5EE', border: '1px solid rgba(15,107,63,0.3)', borderRadius: 8 }}>
          ✓ Nobody has opted out yet.
        </div>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {entries.map(e => (
            <li key={e.id} style={{ background: e.reAddedAt ? '#E7F5EE' : '#FEF2F2', border: `1px solid ${e.reAddedAt ? 'rgba(15,107,63,0.3)' : '#FCA5A5'}`, borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
                <div>
                  <strong style={{ color: e.reAddedAt ? '#0F6B3F' : '#991B1B', fontSize: 14 }}>
                    {e.name || 'Seller'} · <span style={{ fontFamily: 'monospace' }}>{e.e164 || `+1${e.phone}`}</span>
                  </strong>
                  <div style={{ fontSize: 11, color: MID, marginTop: 4 }}>
                    {e.reAddedAt
                      ? <>Re-added {e.reAddedAt.toDate?.()?.toLocaleString?.() || ''} — note: "{e.reAddedConfirmation}"</>
                      : <>Opted out {e.optedOutAt?.toDate?.()?.toLocaleString?.() || ''} via "{e.matchedKeyword}"</>}
                  </div>
                </div>
                {!e.reAddedAt && (
                  <button onClick={() => setClearing(e)} style={{ background: '#fff', border: '1px solid #FCA5A5', color: '#991B1B', borderRadius: 999, padding: '6px 14px', fontSize: 11, fontWeight: 500, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}>↩ Re-add</button>
                )}
              </div>
              {!e.reAddedAt && (
                <div style={{ fontSize: 12, color: '#1C1C1C', fontStyle: 'italic', padding: '8px 12px', background: '#fff', borderRadius: 6, lineHeight: 1.5 }}>
                  "{e.messageText || e.matchedKeyword}"
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {clearing && (
        <ClearOptOutPrompt
          entry={clearing}
          onCancel={() => setClearing(null)}
          onConfirm={(note) => reAdd(clearing, note)}
        />
      )}
    </Card>
  );
}

function ClearOptOutPrompt({ entry, onCancel, onConfirm }) {
  const [note, setNote] = useState('');
  const valid = note.trim().length >= 10;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(28,28,28,0.55)', zIndex: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 14, maxWidth: 480, width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.3)', overflow: 'hidden', fontFamily: "'DM Sans', sans-serif" }}>
        <div style={{ padding: '14px 22px', background: '#FEF2F2', borderBottom: '1px solid #FCA5A5' }}>
          <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#991B1B' }}>Re-add opt-out</div>
        </div>
        <div style={{ padding: 22 }}>
          <p style={{ margin: '0 0 12px', color: BURG_DK, fontSize: 14, lineHeight: 1.6 }}>
            Confirm <strong>{entry.name || entry.phone}</strong> has explicitly told you they want to be contacted again.
          </p>
          <textarea
            value={note}
            onChange={ev => setNote(ev.target.value)}
            rows={3}
            placeholder='e.g. "Verbal yes from seller during call on 5/12 — they reconsidered selling."'
            style={{ width: '100%', padding: '12px 14px', border: `1px solid ${BORDER}`, borderRadius: 10, fontSize: 14, fontFamily: "'DM Sans', sans-serif", boxSizing: 'border-box', resize: 'vertical' }}
          />
          <div style={{ fontSize: 11, color: LIGHT, marginTop: 6 }}>Min 10 characters. Saved alongside your name + timestamp.</div>
        </div>
        <div style={{ padding: '14px 22px', borderTop: `1px solid ${BORDER}`, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>
          <Btn variant="danger" disabled={!valid} onClick={() => onConfirm(note.trim())}>✓ Re-add</Btn>
        </div>
      </div>
    </div>
  );
}

// Pulls recent Quo messages and back-fills any that the webhook
// missed. Useful when the webhook wasn't subscribed yet, the
// seller's phone was added to the lead after they replied, or
// any other gap in the live pipeline.
function SyncQuoMessagesCard() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const sync = async () => {
    setBusy(true); setResult(null);
    try {
      const fn = httpsCallable(functions, 'syncQuoMessages');
      const res = await fn({});
      setResult(res.data);
    } catch (e) {
      setResult({ ok: false, message: e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Sync recent SMS from Quo" right={<Pill color="gold">Admin only</Pill>}>
      <div style={{ fontSize: 13, color: MID, lineHeight: 1.65, marginBottom: 14 }}>
        Pulls the last 50 messages between your business number and every lead's owner phones,
        then back-fills any messages our live webhook missed. Safe to click any time — dedupes
        by Quo's message ID, so existing thread entries aren't duplicated.
      </div>
      <Btn variant="gold" onClick={sync} disabled={busy}>
        {busy ? 'Syncing…' : '🔄 Sync now'}
      </Btn>
      {result && (
        <div style={{ marginTop: 14, padding: 12, background: result.ok ? '#E7F5EE' : '#FEF2F2', border: `1px solid ${result.ok ? 'rgba(15,107,63,0.3)' : '#FCA5A5'}`, borderRadius: 8, fontSize: 13, color: result.ok ? '#0F6B3F' : '#991B1B' }}>
          {result.ok ? (
            <>
              <strong>✓ Synced.</strong> Checked {result.leadsChecked} lead{result.leadsChecked === 1 ? '' : 's'};
              {' '}updated {result.leadsUpdated} ({result.messagesAdded} new message{result.messagesAdded === 1 ? '' : 's'} added).
              {result.errors?.length > 0 && (
                <div style={{ marginTop: 8, fontSize: 11, color: MID }}>
                  ⚠ {result.errors.length} error{result.errors.length === 1 ? '' : 's'} (first: {result.errors[0].error}).
                </div>
              )}
            </>
          ) : <strong>⚠ {result.message}</strong>}
        </div>
      )}
    </Card>
  );
}

// Recent SMS blocked by the content filter. Owner-only audit view.
// Pulls the most recent 30 flagged attempts so the owner can see who
// tried to send what, retrain or have a conversation as needed.
function ModerationLogCard() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { collection: col, getDocs: gd, query: q, orderBy: ob, limit: lim } = await import('firebase/firestore');
        const snap = await gd(q(col(db, 'moderationLog'), ob('at', 'desc'), lim(30)));
        if (!cancelled) {
          setEntries(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        }
      } catch (e) {
        console.warn('moderationLog read failed:', e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <Card title="SMS content filter — blocked attempts" right={<Pill color="red">Admin only</Pill>}>
      <div style={{ fontSize: 13, color: MID, lineHeight: 1.65, marginBottom: 14 }}>
        Every outbound SMS is scanned for profanity, slurs, threats, and harassment <em>before</em>
        it reaches Quo. Blocked messages never touch the carrier (which protects your A2P approval).
        Recent flagged attempts:
      </div>
      {loading ? (
        <div style={{ fontSize: 13, color: LIGHT, padding: '14px 0' }}>Loading…</div>
      ) : entries.length === 0 ? (
        <div style={{ fontSize: 13, color: '#0F6B3F', padding: '14px 16px', background: '#E7F5EE', border: '1px solid rgba(15,107,63,0.3)', borderRadius: 8 }}>
          ✓ Clean slate — nothing has been flagged yet.
        </div>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {entries.map(e => (
            <li key={e.id} style={{ background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
                <strong style={{ color: '#92400E', fontSize: 13 }}>{e.agentName || 'Agent'}</strong>
                <span style={{ fontSize: 11, color: '#92400E', fontStyle: 'italic' }}>
                  {e.at?.toDate?.()?.toLocaleString?.() || ''}
                </span>
              </div>
              <div style={{ fontSize: 13, color: '#1C1C1C', marginBottom: 6, fontStyle: 'italic', lineHeight: 1.5, padding: '8px 12px', background: '#fff', borderRadius: 6 }}>
                "{e.body}"
              </div>
              <div style={{ fontSize: 11, color: MID, lineHeight: 1.6 }}>
                <strong>To:</strong> <span style={{ fontFamily: 'monospace' }}>{e.recipient || '—'}</span>
                {' · '}
                <strong>Reason:</strong> {e.source === 'blocklist' ? 'Profanity / slur / threat (hardcoded)' : `AI flagged: ${(e.categories || []).join(', ')}`}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// One-time migration tool: rewrites every agent's mailboxAddress from
// @mg.homeiqa.com to @homeiqa.com after the root domain came online.
// Idempotent — safe to click multiple times.
function MigrateMailboxesCard() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setBusy(true); setResult(null);
    try {
      // First find Laketia + Denicia uids to apply proper-case overrides.
      const aSnap = await getDoc(doc(db, 'agents', 'placeholder')); // dummy to ensure imports OK
      const { collection: col, getDocs: gd } = await import('firebase/firestore');
      const all = await gd(col(db, 'agents'));
      const overrides = {};
      all.forEach(d => {
        const a = d.data();
        if (a.email === 'paperwriter20@gmail.com') overrides[d.id] = 'towanjalin@homeiqa.com';
        if ((a.name || '').toLowerCase().includes('denicia')) overrides[d.id] = 'Denicia.W@homeiqa.com';
      });

      const fn = httpsCallable(functions, 'migrateMailboxesToRoot');
      const res = await fn({ overrides });
      setResult(res.data);
    } catch (e) {
      setResult({ ok: false, message: e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Migrate mailboxes to homeiqa.com" right={<Pill color="gold">One-time · Admin only</Pill>}>
      <div style={{ fontSize: 13, color: MID, lineHeight: 1.65, marginBottom: 14 }}>
        After verifying the root <code>homeiqa.com</code> domain in Mailgun, click this once to rewrite every agent's mailbox address from <code>@mg.homeiqa.com</code> → <code>@homeiqa.com</code>. Sets <strong>towanjalin@homeiqa.com</strong> and <strong>Denicia.W@homeiqa.com</strong>. Idempotent — safe to re-run.
      </div>
      <Btn variant="gold" onClick={run} disabled={busy}>
        {busy ? 'Migrating…' : '🔄 Run mailbox migration'}
      </Btn>
      {result && (
        <div style={{ marginTop: 14, padding: 12, background: result.ok ? '#E7F5EE' : '#FEF2F2', border: `1px solid ${result.ok ? 'rgba(15,107,63,0.3)' : '#FCA5A5'}`, borderRadius: 8, fontSize: 12, color: result.ok ? '#0F6B3F' : '#991B1B' }}>
          {result.ok ? (
            <>
              <strong>✓ Updated {result.updatedCount} mailbox{result.updatedCount === 1 ? '' : 'es'}.</strong>
              {result.updated && result.updated.length > 0 && (
                <ul style={{ margin: '8px 0 0', paddingLeft: 20, lineHeight: 1.7, color: '#1C1C1C' }}>
                  {result.updated.map((u, i) => (
                    <li key={i}>
                      <strong>{u.name}</strong>: <code>{u.from}</code> → <code>{u.to}</code>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <strong>⚠ {result.message}</strong>
          )}
        </div>
      )}
    </Card>
  );
}

// Cryptographically random, human-readable password: 16 chars in 4 groups
// of 4, no confusable characters (0/O/1/l/I).
function generateSharePassword() {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
  const buf = new Uint8Array(16);
  (window.crypto || window.msCrypto).getRandomValues(buf);
  let raw = '';
  for (let i = 0; i < 16; i++) raw += alphabet[buf[i] % alphabet.length];
  return `${raw.slice(0,4)}-${raw.slice(4,8)}-${raw.slice(8,12)}-${raw.slice(12,16)}`;
}

function JvShareCard({ profile }) {
  const [tokenId, setTokenId] = useState('');
  const [password, setPassword] = useState(() => generateSharePassword());
  const [tagFilter, setTagFilter] = useState('JV');
  const [partnerName, setPartnerName] = useState('Rashad Blackman');
  const [partnerEmail, setPartnerEmail] = useState('rashad.blackman08@gmail.com');
  const [creating, setCreating] = useState(false);
  const [msg, setMsg] = useState('');
  const [showPw, setShowPw] = useState(true); // show by default since auto-gen'd
  const regeneratePassword = () => setPassword(generateSharePassword());

  const create = async () => {
    if (password.trim().length < 6) { setMsg('Password must be at least 6 characters.'); return; }
    setCreating(true); setMsg('');
    try {
      const fn = httpsCallable(functions, 'createJvShareToken');
      const res = await fn({
        tokenId: tokenId || undefined,
        password: password.trim(),
        tagFilter: tagFilter.trim() || 'JV',
        partnerName: partnerName.trim(),
        partnerEmail: partnerEmail.trim()
      });
      setTokenId(res.data.tokenId);
      setMsg('✓ Share ready. Copy the link + password below and send them separately (text one, email the other).');
    } catch (e) {
      setMsg(`Failed: ${e.message}`);
    } finally {
      setCreating(false);
    }
  };

  const revoke = async () => {
    if (!tokenId) return;
    if (!window.confirm('Revoke this share link? Anyone with the URL + password will lose access immediately.')) return;
    try {
      const fn = httpsCallable(functions, 'setJvShareActive');
      await fn({ tokenId, active: false });
      setTokenId(''); setPassword(''); setMsg('Revoked.');
    } catch (e) {
      setMsg(`Revoke failed: ${e.message}`);
    }
  };

  const shareUrl = tokenId ? `${window.location.origin}/jv-status/${tokenId}` : '';

  return (
    <Card title="JV partner share" right={<Pill color="gold">Admin only</Pill>}>
      <div style={{ fontSize: 13, color: MID, lineHeight: 1.65, marginBottom: 14 }}>
        Generates a private URL your JV partner can open. They see a read-only table of every lead with your <strong>{tagFilter || 'JV'}</strong> tag: address, owner, whether you've contacted them + date, and the expected next contact date based on today's automation schedule.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="JV partner name"><Input value={partnerName} onChange={e => setPartnerName(e.target.value)} placeholder="Rashad Blackman" /></Field>
        <Field label="JV partner email"><Input type="email" value={partnerEmail} onChange={e => setPartnerEmail(e.target.value)} placeholder="rashad.blackman08@gmail.com" /></Field>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px', gap: 12 }}>
        <Field label="Password (auto-generated)" hint="Regenerate anytime. Copy this + the URL and send them to the partner via different channels.">
          <div style={{ display: 'flex', gap: 6 }}>
            <Input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Auto-generated" style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }} />
            <button type="button" onClick={() => setShowPw(s => !s)} title={showPw ? 'Hide' : 'Show'} style={{ padding: '0 10px', fontSize: 12, background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 8, cursor: 'pointer' }}>{showPw ? '👁' : '👁‍🗨'}</button>
            <button type="button" onClick={regeneratePassword} title="Generate a new password" style={{ padding: '0 10px', fontSize: 12, background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 8, cursor: 'pointer' }}>↻</button>
            <button type="button" onClick={() => { navigator.clipboard.writeText(password); setMsg('✓ Password copied.'); setTimeout(() => setMsg(''), 2000); }} title="Copy password" style={{ padding: '0 10px', fontSize: 12, background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 8, cursor: 'pointer' }}>📋</button>
          </div>
        </Field>
        <Field label="Tag to share">
          <Input value={tagFilter} onChange={e => setTagFilter(e.target.value)} placeholder="JV" />
        </Field>
      </div>

      {shareUrl && (
        <div style={{ background: CREAM, borderRadius: 10, padding: '12px 14px', marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: LIGHT, fontWeight: 600, marginBottom: 4 }}>SHARE URL</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <code style={{ fontSize: 12, wordBreak: 'break-all', flex: 1, color: BURG_DK }}>{shareUrl}</code>
            <button type="button" onClick={() => { navigator.clipboard.writeText(shareUrl); setMsg('✓ URL copied.'); }}
              style={{ padding: '4px 12px', fontSize: 12, background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 6, cursor: 'pointer' }}>Copy</button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        {msg && <span style={{ fontSize: 12, color: /failed|min/i.test(msg) ? '#B91C1C' : '#166534' }}>{msg}</span>}
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          {tokenId && <Btn variant="danger" onClick={revoke}>Revoke</Btn>}
          <Btn onClick={create} disabled={creating || !password.trim()}>
            {creating ? 'Saving…' : (tokenId ? 'Rotate password' : 'Create share')}
          </Btn>
        </div>
      </div>
    </Card>
  );
}

function OutreachSettingsCard({ profile }) {
  // Three day-1 variants — rotate by day-of-year so consecutive days
  // never send the same body. Reduces carrier pattern-matching risk.
  const DEFAULT_VARIANTS = [
    "Hi {ownerFirstName}, quick question about {address} — do you still own the house? Reply STOP to opt out.",
    "Hey, reaching out about {address}. Would you have a moment to chat sometime this week? Reply STOP to opt out.",
    "{ownerFirstName}, hi. Is {address} still in the family? Would love to connect briefly. Reply STOP to opt out."
  ];
  const DEFAULT_FOLLOWUP_SMS = "Hey, just checking back in on the text from last week. Let me know if you would be open to a quick chat about your property at {address}. Reply STOP to opt out.";
  const DEFAULT_FOLLOWUP_EMAIL_SUBJECT = "About your property at {address}";
  const DEFAULT_FOLLOWUP_EMAIL = "Hi {ownerFirstName},\n\nI'm Laketia. I'm sending you an email because I'm not sure if I have the correct cell phone number for you, but I'm interested in your property at {address} and wanted to know: would you consider a cash offer? Happy to work around your timeline.\n\nIf you'd rather not hear from me again, just reply and I'll take you off the list.\n\nLaketia";
  const [form, setForm] = useState({
    enabled: false,
    templates: DEFAULT_VARIANTS,
    followupSmsTemplate: DEFAULT_FOLLOWUP_SMS,
    followupEmailSubject: DEFAULT_FOLLOWUP_EMAIL_SUBJECT,
    followupEmailTemplate: DEFAULT_FOLLOWUP_EMAIL,
    dailyBatchSize: 25,
    tagFilter: 'JV',
    startDate: ''
  });
  const [lastStats, setLastStats] = useState(null);
  const [lastRunAt, setLastRunAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'settings', 'outreach'), snap => {
      if (snap.exists()) {
        const d = snap.data();
        const loadedVariants = Array.isArray(d.templates) && d.templates.length
          ? d.templates
          : (d.template ? [d.template] : DEFAULT_VARIANTS);
        // Always pad to 3 slots so the UI has all three editors visible.
        const padded = [...loadedVariants];
        while (padded.length < 3) padded.push(DEFAULT_VARIANTS[padded.length] || '');
        setForm(f => ({
          ...f,
          enabled: !!d.enabled,
          templates: padded.slice(0, 3),
          followupSmsTemplate: d.followupSmsTemplate || DEFAULT_FOLLOWUP_SMS,
          followupEmailSubject: d.followupEmailSubject || DEFAULT_FOLLOWUP_EMAIL_SUBJECT,
          followupEmailTemplate: d.followupEmailTemplate || DEFAULT_FOLLOWUP_EMAIL,
          dailyBatchSize: d.dailyBatchSize || 25,
          tagFilter: d.tagFilter || 'JV',
          startDate: d.startDate?.toDate?.().toISOString().slice(0, 10) || ''
        }));
        setLastStats(d.lastRunStats || null);
        setLastRunAt(d.lastRunAt?.toDate?.() || null);
      }
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
    // eslint-disable-next-line
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      const cleanedVariants = form.templates.map(t => String(t || '').trim()).filter(Boolean);
      const payload = {
        enabled: !!form.enabled,
        templates: cleanedVariants,
        // Keep legacy `template` in sync (first variant) so any older
        // reader still finds something. Removed once nothing reads it.
        template: cleanedVariants[0] || '',
        followupSmsTemplate: form.followupSmsTemplate.trim(),
        followupEmailSubject: form.followupEmailSubject.trim(),
        followupEmailTemplate: form.followupEmailTemplate.trim(),
        dailyBatchSize: Math.max(1, Math.min(60, Number(form.dailyBatchSize) || 25)),
        tagFilter: form.tagFilter.trim() || 'JV',
        pipelineUid: profile.uid,
        updatedAt: serverTimestamp()
      };
      if (form.startDate) payload.startDate = Timestamp.fromDate(new Date(form.startDate));
      await setDoc(doc(db, 'settings', 'outreach'), payload, { merge: true });
      setMsg('✓ Saved.');
      setTimeout(() => setMsg(''), 2200);
    } catch (e) {
      setMsg(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    setRunning(true); setMsg('');
    try {
      const fn = httpsCallable(functions, 'runOutreachBatchNow');
      const res = await fn({});
      const s = res.data || {};
      if (s.skipped) setMsg(`Batch skipped: ${s.reason}. Toggle 'Enabled' and save first, or check your start date.`);
      else setMsg(`✓ Batch ran — sent ${s.sent}, failed ${s.failed}, skipped ${s.skipped}, out of ${s.attempted} attempted (${s.eligible} eligible).`);
    } catch (e) {
      setMsg(`Run failed: ${e.message}`);
    } finally {
      setRunning(false);
    }
  };

  if (loading) return <Card title="Daily outreach batch"><div style={{ color: LIGHT, padding: 20 }}>Loading…</div></Card>;

  return (
    <Card title="Daily outreach batch" right={<Pill color={form.enabled ? 'green' : 'gray'}>{form.enabled ? 'On' : 'Off'}</Pill>}>
      <div style={{ fontSize: 13, color: MID, lineHeight: 1.65, marginBottom: 14 }}>
        Fires once a day at <strong>8:00 AM Pacific</strong>. Picks up to <strong>batch-size</strong> JV-tagged leads that haven't been successfully contacted yet and spreads the sends randomly between <strong>8am and 11am PT</strong> so the number doesn't get carrier-flagged. Skips any recipient whose local time is outside 8am-9pm (TCPA). "Reply STOP to opt out." is auto-appended. When a number bounces, the lead rolls to its next available phone on the 7-day follow-up run.
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, cursor: 'pointer' }}>
        <input type="checkbox" checked={form.enabled} onChange={e => set('enabled', e.target.checked)} style={{ width: 18, height: 18 }} />
        <span style={{ fontSize: 14, fontWeight: 500, color: BURG_DK }}>Enabled — send the batch every morning at 9:13 AM ET</span>
      </label>

      <div style={{ background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 10, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: '#78350F' }}>
        <strong>Rotation:</strong> the three day-1 variants below rotate by day of year. Consecutive days never send the same body, which is what keeps carriers from pattern-flagging the number. Fill in all 3 — leave one blank only if you want to skip that slot.
      </div>
      {[0, 1, 2].map(i => (
        <Field key={i}
          label={`Day-1 SMS variant ${String.fromCharCode(65 + i)}`}
          hint={i === 0 ? "Placeholders: {address}, {ownerFirstName}, {city}, {state}, {streetName}. 'Reply STOP to opt out.' auto-appended." : ''}
        >
          <textarea
            value={form.templates[i] || ''}
            onChange={e => setForm(f => {
              const next = [...f.templates]; next[i] = e.target.value; return { ...f, templates: next };
            })}
            rows={3}
            style={{ width: '100%', padding: 12, border: `1px solid ${BORDER}`, borderRadius: 10, fontSize: 14, fontFamily: "'Inter', sans-serif", resize: 'vertical' }} />
        </Field>
      ))}

      <Field label="7-day follow-up SMS" hint="Sent one week later to the next-in-line phone if the first didn't reply. STOP line auto-appended.">
        <textarea value={form.followupSmsTemplate} onChange={e => set('followupSmsTemplate', e.target.value)} rows={3}
          style={{ width: '100%', padding: 12, border: `1px solid ${BORDER}`, borderRadius: 10, fontSize: 14, fontFamily: "'Inter', sans-serif", resize: 'vertical' }} />
      </Field>

      <Field label="Follow-up email subject" hint="Same-time-as follow-up SMS. Uses the owner's Email 1.">
        <Input value={form.followupEmailSubject} onChange={e => set('followupEmailSubject', e.target.value)} />
      </Field>

      <Field label="Follow-up email body" hint="From: T. Laketia Jones <T.Laketia.J@homeiqa.com>. No STOP appended (unsubscribe language should be in the body).">
        <textarea value={form.followupEmailTemplate} onChange={e => set('followupEmailTemplate', e.target.value)} rows={7}
          style={{ width: '100%', padding: 12, border: `1px solid ${BORDER}`, borderRadius: 10, fontSize: 14, fontFamily: "'Inter', sans-serif", resize: 'vertical', whiteSpace: 'pre-wrap' }} />
      </Field>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        <Field label="Batch size / day" hint="Max 60. Higher = faster but more carrier risk."><Input type="number" min="1" max="60" value={form.dailyBatchSize} onChange={e => set('dailyBatchSize', e.target.value)} /></Field>
        <Field label="Tag filter" hint="Only leads with this tag will get texted."><Input value={form.tagFilter} onChange={e => set('tagFilter', e.target.value)} placeholder="JV" /></Field>
        <Field label="Start date" hint="Batch does nothing before this date."><Input type="date" value={form.startDate} onChange={e => set('startDate', e.target.value)} /></Field>
      </div>

      {lastRunAt && (
        <div style={{ fontSize: 12, color: MID, background: CREAM, borderRadius: 8, padding: '10px 14px', margin: '4px 0 14px' }}>
          <strong>Last run:</strong> {lastRunAt.toLocaleString()}
          {lastStats && <> — sent {lastStats.sent || 0}, failed {lastStats.failed || 0}, skipped {lastStats.skipped || 0} (of {lastStats.attempted || 0} attempted · {lastStats.eligible || 0} eligible).</>}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        {msg && <span style={{ fontSize: 12, color: /failed|skipped/i.test(msg) ? '#B91C1C' : '#166534' }}>{msg}</span>}
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          <Btn variant="ghost" onClick={runNow} disabled={running || !form.enabled}>{running ? 'Running…' : '▶ Run batch now'}</Btn>
          <Btn onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Btn>
        </div>
      </div>
    </Card>
  );
}

// Minimal RFC 4180-ish CSV parser. Handles quoted fields with commas and
// escaped double-quotes. Not a full spec parser but covers PropStream /
// BatchLeads / DealMachine / Google Sheets exports reliably.
function parseCsv(text) {
  const rows = [];
  let row = [], field = '', inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"' && text[i + 1] === '"') { field += '"'; i++; }
      else if (c === '"') inQuotes = false;
      else field += c;
    } else {
      if (c === '"') inQuotes = true;
      else if (c === ',') { row.push(field); field = ''; }
      else if (c === '\r') { /* skip */ }
      else if (c === '\n') { row.push(field); rows.push(row); row = []; field = ''; }
      else field += c;
    }
  }
  if (field !== '' || row.length) { row.push(field); rows.push(row); }
  return rows;
}

function ImportLeadsCard({ profile }) {
  const [sourceMode, setSourceMode] = useState('url'); // 'url' | 'paste'
  const [sheetUrl, setSheetUrl] = useState('');
  const [pastedCsv, setPastedCsv] = useState('');
  const [preview, setPreview] = useState(null); // { headers, rows, mapped, skipped, dupes }
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  // Turn a Google Sheets edit URL into its CSV export URL.
  const toCsvExportUrl = (url) => {
    const m = String(url || '').match(/\/spreadsheets\/d\/([a-zA-Z0-9_-]+)/);
    if (!m) return null;
    const gid = (url.match(/[#&?]gid=(\d+)/) || [])[1] || '0';
    return `https://docs.google.com/spreadsheets/d/${m[1]}/export?format=csv&gid=${gid}`;
  };

  const numOrNull = v => {
    const n = Number(String(v || '').replace(/[^\d.-]/g, ''));
    return isFinite(n) && n !== 0 ? n : null;
  };
  const cleanPhone = p => String(p || '').replace(/[^\d]/g, '').slice(-10);

  const rowToLead = (headers, row) => {
    const get = (name) => {
      const idx = headers.findIndex(h => h.toLowerCase().trim() === name.toLowerCase().trim());
      return idx >= 0 ? (row[idx] || '').trim() : '';
    };
    const doNotMail = get('Do Not Mail').toLowerCase();
    const line1 = get('Address');
    if (!line1) return { skip: 'no-address' };
    if (doNotMail === 'yes') return { skip: 'do-not-mail' };

    const o1 = [get('Owner 1 First Name'), get('Owner 1 Last Name')].filter(Boolean).join(' ');
    const o2 = [get('Owner 2 First Name'), get('Owner 2 Last Name')].filter(Boolean).join(' ');
    // Priority order: the sheet's "Primary Numbers" column (best contact
    // flagged by the data vendor) → mobiles → landlines. Deduped so the
    // same number doesn't appear twice.
    const parseNums = (s) => String(s || '')
      .split(/[,;|]/)
      .map(cleanPhone)
      .filter(p => p.length === 10);
    const primaryNums = parseNums(get('Primary Numbers'));
    const mobileNums  = ['Mobile 1', 'Mobile 2', 'Mobile 3', 'Mobile 4'].flatMap(k => parseNums(get(k)));
    const landlineNums = ['Landline 1', 'Landline 2', 'Landline 3', 'Landline 4'].flatMap(k => parseNums(get(k)));
    const seenPhones = new Set();
    const phones = [];
    [...primaryNums, ...mobileNums, ...landlineNums].forEach(p => {
      if (!seenPhones.has(p)) { seenPhones.add(p); phones.push(p); }
    });
    const emails = ['Email 1', 'Email 2', 'Email 3'].map(k => get(k)).filter(Boolean);
    const owners = [];
    owners.push({
      name: o1 || 'Property Owner',
      phone: phones[0] || '',
      email: emails[0] || '',
      additionalPhones: phones.slice(1),
      additionalEmails: emails.slice(1)
    });
    if (o2) owners.push({ name: o2, phone: '', email: '' });

    // Build the "Import" note — every column from the sheet that doesn't
    // have a dedicated structured slot on the lead lives here, so nothing
    // from the source is lost. Bolded headers via markdown **text**.
    const noteLines = [];
    const putNote = (label, value) => {
      const v = String(value == null ? '' : value).trim();
      if (v) noteLines.push(`**${label}:** ${v}`);
    };
    noteLines.push(`**Imported ${new Date().toLocaleDateString()} from Google Sheet**`);
    noteLines.push('');

    // Mailing (owner's contact address — often different from property).
    const mailingLine = get('Mailing Address');
    if (mailingLine) {
      putNote('Mailing Address', [
        mailingLine,
        get('Mailing Unit #'),
        [get('Mailing City'), get('Mailing State')].filter(Boolean).join(', '),
        get('Mailing Zip')
      ].filter(Boolean).join(' '));
      putNote('Mailing County', get('Mailing County'));
    }
    putNote('Mailing Care of Name', get('Mailing Care of Name'));
    putNote('Possible Relative', get('Possible Relative'));
    putNote('Primary Numbers (best contact)', get('Primary Numbers'));
    putNote('Property Status', get('Property Status'));
    putNote('Source-list Notes', get('Notes'));

    // Extra contact numbers not on the primary owner slot.
    const extraPhonesRaw = ['Mobile 2', 'Mobile 3', 'Mobile 4', 'Landline 1', 'Landline 2', 'Landline 3', 'Landline 4']
      .map(k => get(k)).filter(Boolean);
    if (extraPhonesRaw.length) putNote('Additional Phone Numbers', extraPhonesRaw.join(' · '));
    const extraEmailsRaw = ['Email 2', 'Email 3'].map(k => get(k)).filter(Boolean);
    if (extraEmailsRaw.length) putNote('Additional Emails', extraEmailsRaw.join(' · '));

    // Loans / equity detail.
    putNote('Total Open Loans', get('Total Open Loans'));
    putNote('Est. Remaining Loan Balance', get('Est. Remaining balance of Open Loans'));
    putNote('Lien Amount', get('Lien Amount'));
    putNote('Foreclosure Factor', get('Foreclosure Factor'));

    // Condition breakdown.
    putNote('Overall Condition', get('Total Condition'));
    putNote('Interior Condition', get('Interior Condition'));
    putNote('Exterior Condition', get('Exterior Condition'));
    putNote('Bathroom Condition', get('Bathroom Condition'));
    putNote('Kitchen Condition', get('Kitchen Condition'));

    // MLS history.
    putNote('MLS Date', get('MLS Date'));
    putNote('MLS Amount', get('MLS Amount'));

    // Source tracking (where the lead came from).
    putNote('Marketing List', get('Marketing Lists'));
    putNote('Date Added to List', get('Date Added to List'));
    putNote('Method of Add', get('Method of Add'));

    const importNote = {
      type: 'Other',
      body: noteLines.join('\n'),
      createdAt: Timestamp.now(),
      author: { uid: profile.uid, name: profile.name || 'Import' }
    };

    // Tags: JV always, plus the source marketing list if provided.
    const tags = ['JV'];
    const marketingList = get('Marketing Lists');
    if (marketingList && marketingList.toUpperCase() !== 'JV') tags.push(marketingList);

    const lead = {
      address: {
        line1,
        unit: get('Unit #'),
        city: get('City'),
        state: get('State'),
        zip: get('Zip'),
        county: get('County')
      },
      owners,
      propertyData: {
        apn: get('APN'),
        ownerOccupied: get('Owner Occupied').toLowerCase() === 'yes',
        propertyType: get('Property Type'),
        beds: numOrNull(get('Bedrooms')),
        baths: numOrNull(get('Total Bathrooms')),
        sqft: numOrNull(get('Building Sqft')),
        lotSize: numOrNull(get('Lot Size Sqft')),
        yearBuilt: numOrNull(get('Effective Year Built')),
        assessedValue: numOrNull(get('Total Assessed Value')),
        lastSaleDate: get('Last Sale Recording Date') || null,
        lastSalePrice: numOrNull(get('Last Sale Amount')),
        estValue: numOrNull(get('Est. Value')),
        estEquity: numOrNull(get('Est. Equity')),
        estLtv: numOrNull(get('Est. Loan-to-Value')),
        mlsStatus: get('MLS Status') || null
      },
      status: 'New',
      source: 'sheet-import',
      tags,
      notes: [importNote],
      submittedBy: { uid: profile.uid, name: profile.name || '', email: profile.email || '' },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    return { lead };
  };

  const runPreview = async () => {
    setStatus(''); setPreview(null); setBusy(true);
    try {
      let csvText = '';
      if (sourceMode === 'url') {
        const csvUrl = toCsvExportUrl(sheetUrl);
        if (!csvUrl) { setStatus('That doesn\'t look like a Google Sheets URL.'); return; }
        const r = await fetch(csvUrl);
        if (!r.ok) throw new Error(`Fetch failed (${r.status}). Make sure the sheet sharing is "Anyone with the link — Viewer".`);
        csvText = await r.text();
      } else {
        csvText = pastedCsv;
      }
      const rows = parseCsv(csvText).filter(r => r.some(c => c.trim()));
      if (rows.length < 2) { setStatus('No data rows found.'); return; }
      const headers = rows[0].map(h => h.trim());
      const body = rows.slice(1);

      // Local dedupe against existing leads in the pipeline.
      const existingSnap = await getDocs(collection(db, 'agents', profile.uid, 'leads'));
      const existingKeys = new Set(existingSnap.docs.map(d => {
        const a = d.data().address || {};
        return `${(a.line1 || '').toLowerCase().trim()}|${(a.zip || '').trim()}`;
      }));

      const mapped = [];
      const skipped = { 'no-address': 0, 'do-not-mail': 0 };
      const dupes = [];
      body.forEach((row, i) => {
        const res = rowToLead(headers, row);
        if (res.skip) { skipped[res.skip] = (skipped[res.skip] || 0) + 1; return; }
        const key = `${res.lead.address.line1.toLowerCase().trim()}|${(res.lead.address.zip || '').trim()}`;
        if (existingKeys.has(key)) { dupes.push({ i, addr: res.lead.address.line1 }); return; }
        existingKeys.add(key);
        mapped.push(res.lead);
      });
      setPreview({ headers, rows: body, mapped, skipped, dupes });
      setStatus(`Ready: ${mapped.length} new leads · ${skipped['do-not-mail']} skipped (DNM) · ${skipped['no-address']} skipped (no address) · ${dupes.length} already in pipeline.`);
    } catch (e) {
      setStatus(`Preview failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const runImport = async () => {
    if (!preview?.mapped?.length) return;
    setBusy(true);
    setProgress({ done: 0, total: preview.mapped.length });
    try {
      // Firestore batches max out at 500. Chunk at 400 for safety headroom.
      const leadsCol = collection(db, 'agents', profile.uid, 'leads');
      const CHUNK = 400;
      for (let i = 0; i < preview.mapped.length; i += CHUNK) {
        const batch = writeBatch(db);
        const slice = preview.mapped.slice(i, i + CHUNK);
        slice.forEach(lead => batch.set(doc(leadsCol), lead));
        await batch.commit();
        setProgress({ done: Math.min(i + slice.length, preview.mapped.length), total: preview.mapped.length });
      }
      setStatus(`✓ Imported ${preview.mapped.length} leads into your pipeline.`);
      setPreview(null);
      setSheetUrl(''); setPastedCsv('');
    } catch (e) {
      setStatus(`Import failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Import from Google Sheet / CSV" right={<Pill color="gold">Admin only</Pill>}>
      <div style={{ fontSize: 13, color: MID, lineHeight: 1.65, marginBottom: 14 }}>
        Pulls property + owner + phone/email columns straight into your Leads pipeline. Do-Not-Mail rows are skipped automatically; existing addresses are deduped.
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <button type="button" onClick={() => setSourceMode('url')}
          style={{ padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: "'Inter', sans-serif", border: `1px solid ${sourceMode === 'url' ? BURG : BORDER}`, background: sourceMode === 'url' ? BURG : '#fff', color: sourceMode === 'url' ? '#fff' : BURG_DK }}>
          Google Sheet URL
        </button>
        <button type="button" onClick={() => setSourceMode('paste')}
          style={{ padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: "'Inter', sans-serif", border: `1px solid ${sourceMode === 'paste' ? BURG : BORDER}`, background: sourceMode === 'paste' ? BURG : '#fff', color: sourceMode === 'paste' ? '#fff' : BURG_DK }}>
          Paste CSV
        </button>
      </div>

      {sourceMode === 'url' ? (
        <Field label="Google Sheet URL" hint="Sharing must be 'Anyone with the link → Viewer'. I'll convert to the CSV export URL automatically.">
          <Input value={sheetUrl} onChange={e => setSheetUrl(e.target.value)} placeholder="https://docs.google.com/spreadsheets/d/..." />
        </Field>
      ) : (
        <Field label="Paste CSV text">
          <textarea value={pastedCsv} onChange={e => setPastedCsv(e.target.value)} rows={6}
            placeholder="Address,City,State,Zip,Owner 1 First Name,Owner 1 Last Name,Mobile 1,..."
            style={{ width: '100%', padding: 12, border: `1px solid ${BORDER}`, borderRadius: 10, fontSize: 12, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', resize: 'vertical' }} />
        </Field>
      )}

      <div style={{ display: 'flex', gap: 10, marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <Btn variant="ghost" onClick={runPreview} disabled={busy || (sourceMode === 'url' ? !sheetUrl.trim() : !pastedCsv.trim())}>
          {busy && !progress.total ? 'Reading…' : '👁 Preview'}
        </Btn>
        {preview?.mapped?.length > 0 && (
          <Btn variant="gold" onClick={runImport} disabled={busy}>
            {busy && progress.total ? `Importing… ${progress.done}/${progress.total}` : `⬆ Import ${preview.mapped.length} lead${preview.mapped.length === 1 ? '' : 's'}`}
          </Btn>
        )}
        {status && (
          <span style={{ fontSize: 12, color: /failed|no data|doesn't look/i.test(status) ? '#B91C1C' : (/✓|Ready/i.test(status) ? '#166534' : MID) }}>{status}</span>
        )}
      </div>

      {preview?.mapped?.length > 0 && (
        <div style={{ marginTop: 14, background: CREAM, borderRadius: 10, padding: 14, fontSize: 12, color: BURG_DK }}>
          <strong>First 3 to be imported:</strong>
          <ul style={{ margin: '8px 0 0', paddingLeft: 18, lineHeight: 1.6 }}>
            {preview.mapped.slice(0, 3).map((l, i) => (
              <li key={i}>
                {l.address.line1}, {l.address.city}, {l.address.state} {l.address.zip}
                {l.owners[0]?.name !== 'Property Owner' && <span style={{ color: MID }}> · {l.owners[0].name}</span>}
                {l.owners[0]?.phone && <span style={{ color: MID }}> · {l.owners[0].phone}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

function SeedSampleLeadCard() {
  const navigate = useNavigate();
  const [seeding, setSeeding] = useState(false);
  const [result, setResult] = useState(null);

  const seed = async () => {
    setSeeding(true); setResult(null);
    try {
      const fn = httpsCallable(functions, 'seedHelenStLead');
      const res = await fn({});
      setResult(res.data);
    } catch (e) {
      setResult({ ok: false, message: e.message });
    } finally {
      setSeeding(false);
    }
  };

  return (
    <Card title="Sample lead — 537 Helen St, Columbus OH" right={<Pill color="gold">Admin only</Pill>}>
      <div style={{ fontSize: 13, color: MID, lineHeight: 1.65, marginBottom: 14 }}>
        Click below to seed a fully populated test lead — owner phone, asking price, beds/baths/sqft, year built, sale + tax history, schools, owner financing terms, the full FSBO description. Idempotent: safe to click twice (returns the existing lead the second time).
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <Btn variant="gold" onClick={seed} disabled={seeding}>
          {seeding ? 'Seeding…' : '+ Seed Helen St lead'}
        </Btn>
        {result?.ok && (
          <Btn variant="ghost" onClick={() => navigate(`/agent/leads/${result.leadId}`)}>
            Open lead →
          </Btn>
        )}
      </div>
      {result && (
        <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 4, fontSize: 12, ...(result.ok
          ? { background: '#E7F5EE', border: '1px solid rgba(15,107,63,0.25)', color: '#0F6B3F' }
          : { background: '#FEF2F2', border: '1px solid #FCA5A5', color: '#991B1B' })
        }}>
          {result.ok
            ? `${result.alreadyExisted ? '✓ Already in your pipeline' : '✓ Seeded'} · lead ID ${result.leadId}`
            : `Failed: ${result.message}`}
        </div>
      )}
    </Card>
  );
}

function QuoConnectionCard() {
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const webhookUrl = 'https://us-central1-homeiqa-2026.cloudfunctions.net/openphoneWebhook';

  const test = async () => {
    setTesting(true); setResult(null);
    try {
      const fn = httpsCallable(functions, 'testQuoConnection');
      const res = await fn({});
      setResult(res.data);
    } catch (e) {
      setResult({ ok: false, status: 'error', message: e.message });
    } finally {
      setTesting(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const statusPill = result
    ? <Pill color={result.ok ? 'green' : 'red'}>{result.ok ? '✓ Connected' : 'Not connected'}</Pill>
    : <Pill color="gray">Not tested yet</Pill>;

  return (
    <Card title="SMS & Voice provider" right={statusPill}>
      <div style={{ fontSize: 13, color: MID, lineHeight: 1.65, marginBottom: 14 }}>
        Sends outbound SMS from a single dedicated number and routes inbound replies + missed calls + voicemail back to the matching lead via a webhook.
      </div>

      <div style={qs.row}>
        <span style={qs.label}>Inbound webhook URL</span>
        <span style={{ ...qs.value, fontFamily: 'monospace', fontSize: 11 }}>{webhookUrl}</span>
        <button onClick={copy} style={qs.copyBtn}>{copied ? '✓ Copied' : 'Copy'}</button>
      </div>

      {result && (
        <div style={result.ok ? qs.successBox : qs.errBox}>
          <strong>{result.ok ? '✓ ' : '⚠ '}{result.message}</strong>
          {result.ok && result.numbers && result.numbers.length > 0 && (
            <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 12 }}>
              {result.numbers.map(n => (
                <li key={n.id}>
                  <span style={{ fontFamily: 'monospace' }}>{n.number}</span>
                  {n.name ? ` · ${n.name}` : ''}
                </li>
              ))}
            </ul>
          )}
          {!result.ok && result.status === 'unauthorized' && (
            <div style={{ fontSize: 11, color: '#5A5A5A', marginTop: 6 }}>
              Reset the secret: <code>firebase functions:secrets:set QUO_API_KEY</code> then redeploy <code>sendLeadSms</code>.
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, gap: 10 }}>
        <span style={{ fontSize: 11, color: LIGHT }}>
          Set the webhook URL above in your provider's webhook settings. Subscribe to <code>message.received</code>, <code>call.completed</code>, <code>call.missed</code>, <code>voicemail.received</code>.
        </span>
        <Btn onClick={test} disabled={testing}>{testing ? 'Testing…' : '🔌 Test connection'}</Btn>
      </div>
    </Card>
  );
}

const qs = {
  row: { display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: `1px solid ${BORDER}`, fontSize: 12 },
  label: { color: LIGHT, minWidth: 200, textTransform: 'uppercase', fontSize: 10, letterSpacing: '0.08em' },
  value: { color: BURG_DK, flex: 1 },
  copyBtn: { background: 'transparent', border: `1px solid ${BORDER}`, color: BURG, padding: '4px 10px', borderRadius: 999, fontSize: 10, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  successBox: { padding: '12px 14px', background: '#E7F5EE', border: '1px solid rgba(15,107,63,0.25)', borderRadius: 4, color: '#0F6B3F', fontSize: 12, marginTop: 14 },
  errBox: { padding: '12px 14px', background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 4, color: '#991B1B', fontSize: 12, marginTop: 14 }
};

// Admin-only: create a "Lead Agent" record without sending them a
// CRM invite. Generates a placeholder agent doc + intake slug, then
// shows the share link to copy and send. Useful for people you want
// submitting leads but who don't need full CRM access yet (Nicholas,
// VAs, friends, social-media followers).
function AddLeadAgentCard({ adminUid }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null); // { name, slug, link }
  const [err, setErr] = useState('');

  const fmtPhone = (v) => {
    const d = (v || '').replace(/\D/g, '').slice(0, 10);
    if (d.length < 4) return d;
    if (d.length < 7) return `(${d.slice(0,3)}) ${d.slice(3)}`;
    return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
  };

  const create = async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) { setErr('Name is required.'); return; }
    setErr(''); setBusy(true);
    try {
      const { deriveIntakeSlug } = await import('../../lib/auth');
      const slug = deriveIntakeSlug(email, name);
      const id = `placeholder-${slug}`;
      // Reuse the agents collection so the intakeSlug lookup in
      // createPublicLead just works. accessGranted: false flags this
      // as a no-CRM-yet record — they can submit but can't sign in.
      await setDoc(doc(db, 'agents', id), {
        uid: id,
        email: (email || '').trim().toLowerCase() || null,
        name: name.trim(),
        phone: phone.trim() || null,
        role: 'agent',
        status: 'active',
        accessGranted: false,
        defaultCommissionToHomeIQa: 0.25,
        intakeSlug: slug,
        createdBy: adminUid,
        createdAt: serverTimestamp(),
        isPlaceholder: true
      });
      const origin = typeof window !== 'undefined' ? window.location.origin : 'https://homeiqa.com';
      const link = `${origin}/lead-intake/${slug}`;
      setCreated({ name: name.trim(), slug, link });
      setName(''); setEmail(''); setPhone('');
    } catch (e) {
      setErr(`Couldn't create: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(created.link); } catch {}
  };

  return (
    <Card title="Add a Lead Agent (no CRM access)" right={<Pill color="gold">Admin only</Pill>}>
      <div style={{ fontSize: 13, color: MID, lineHeight: 1.65, marginBottom: 16 }}>
        Create a tracked share link for someone you want submitting leads <em>without</em> giving them
        full CRM access yet. They get their own personal lead-intake URL — every property they submit
        through it is credited to them by name + timestamp. When you're ready to give them CRM access,
        invite them with their email above.
      </div>

      {!created ? (
        <form onSubmit={create}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 140px 140px', gap: 10, alignItems: 'end' }}>
            <Field label="Name" required hint="Shown on every lead they submit.">
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="Nicholas O." />
            </Field>
            <Field label="Email (optional)" hint="So you can promote to full CRM later.">
              <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="nicholas@example.com" />
            </Field>
            <Field label="Phone (optional)">
              <Input value={phone} onChange={e => setPhone(fmtPhone(e.target.value))} placeholder="(555) 123-4567" inputMode="tel" />
            </Field>
            <Btn type="submit" variant="gold" disabled={busy || !name.trim()}>{busy ? 'Creating…' : '+ Create & get link'}</Btn>
          </div>
          {err && <div style={{ fontSize: 12, color: '#991B1B', marginTop: 10 }}>{err}</div>}
        </form>
      ) : (
        <div>
          <div style={{ fontSize: 13, color: BURG_DK, marginBottom: 12 }}>
            ✓ <strong>{created.name}</strong> is set up as a Lead Agent. Send them this link:
          </div>
          <div style={shareS.linkBox}>
            <span style={shareS.linkText}>{created.link}</span>
            <button onClick={copy} style={shareS.copyBtn}>Copy</button>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
            <Btn variant="ghost" onClick={() => setCreated(null)}>+ Add another</Btn>
          </div>
        </div>
      )}
    </Card>
  );
}

// Personal Lead Agent share link. Anyone who hits this URL sees the
// public intake form pre-attributed to me — every lead they submit
// is automatically credited to my account.
function ShareLinkCard({ profile }) {
  const [copied, setCopied] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const slug = profile?.intakeSlug;
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://homeiqa.com';
  const link = slug ? `${origin}/lead-intake/${slug}` : '';

  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {}
  };

  // If the agent doesn't have a slug yet (existed before this feature),
  // generate one inline.
  const generate = async () => {
    if (!profile?.uid) return;
    setGenBusy(true);
    try {
      const { deriveIntakeSlug } = await import('../../lib/auth');
      const newSlug = deriveIntakeSlug(profile.email, profile.name);
      await updateDoc(doc(db, 'agents', profile.uid), { intakeSlug: newSlug });
    } finally {
      setGenBusy(false);
    }
  };

  return (
    <Card title="Your Lead Agent share link" right={<Pill color="gold">Per-person tracking</Pill>}>
      <div style={{ fontSize: 13, color: MID, lineHeight: 1.65, marginBottom: 14 }}>
        Send this link to anyone who's helping you find leads — assistants, bird dogs, friends, social-media followers. Every property submitted through this URL is automatically credited to <strong style={{ color: BURG_DK }}>{profile?.name || 'you'}</strong>, time/date stamped, and lands in the HomeIQa pipeline.
      </div>

      {!slug ? (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: LIGHT, flex: 1 }}>You don't have a share link yet.</span>
          <Btn variant="gold" onClick={generate} disabled={genBusy}>{genBusy ? 'Generating…' : 'Generate my link'}</Btn>
        </div>
      ) : (
        <>
          <div style={shareS.linkBox}>
            <span style={shareS.linkText}>{link}</span>
            <button onClick={copy} style={shareS.copyBtn}>{copied ? '✓ Copied' : 'Copy'}</button>
          </div>
          <div style={{ fontSize: 11, color: LIGHT, marginTop: 10, lineHeight: 1.6 }}>
            Tip: post this on Instagram / Facebook bio, text it to friends, drop it in your email signature. Every submission shows in <strong>My Submissions</strong>.
          </div>
        </>
      )}
    </Card>
  );
}

const shareS = {
  linkBox: { display: 'flex', alignItems: 'center', gap: 10, background: CREAM, border: `1px solid ${BORDER}`, borderRadius: 10, padding: '10px 14px' },
  linkText: { flex: 1, fontFamily: 'monospace', fontSize: 13, color: BURG_DK, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  copyBtn: { background: BURG, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }
};

function NotificationsCard({ profile }) {
  const [prefs, setPrefs] = useState(() => getNotifyPrefs());
  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushError, setPushError] = useState('');

  // Detect if THIS browser is already registered for push.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!profile?.uid) return;
      const on = await isPushRegistered(profile.uid).catch(() => false);
      if (!cancelled) setPushOn(on);
    })();
    return () => { cancelled = true; };
  }, [profile?.uid]);

  const update = (patch) => setPrefs(setNotifyPrefs(patch));

  const enableBrowser = async () => {
    const ok = await ensureBrowserNotifyPermission();
    setPermission(typeof Notification !== 'undefined' ? Notification.permission : 'default');
    if (ok) update({ browserNotify: true });
  };

  const togglePush = async (next) => {
    setPushBusy(true); setPushError('');
    try {
      if (next) {
        await registerForPush(profile.uid);
        setPushOn(true);
      } else {
        await unregisterFromPush(profile.uid);
        setPushOn(false);
      }
    } catch (e) {
      setPushError(e.message || 'Could not update push registration.');
    } finally {
      setPushBusy(false);
    }
  };

  const browserBlocked = permission === 'denied';
  const browserGranted = permission === 'granted';

  return (
    <Card title="Notifications" right={<Pill color="gold">This device</Pill>}>
      <div style={{ fontSize: 13, color: MID, lineHeight: 1.65, marginBottom: 18 }}>
        Settings apply to <strong style={{ color: BURG_DK }}>this browser only</strong> — sign in
        on your phone and tweak it there separately.
      </div>

      <ToggleRow
        label="Chime on team chat"
        hint="Plays a soft two-tone ding when a teammate sends a message."
        checked={prefs.chatSound}
        onChange={v => update({ chatSound: v })}
      />
      <ToggleRow
        label="Chime on new email"
        hint="Plays the same chime when a new email lands in the shared or your personal inbox."
        checked={prefs.emailSound}
        onChange={v => update({ emailSound: v })}
      />
      <ToggleRow
        label="Chime on inbound SMS"
        hint="Plays a chime + browser notification the moment a seller replies to a text. Highest priority of the three."
        checked={prefs.smsSound}
        onChange={v => update({ smsSound: v })}
      />
      <ToggleRow
        label="Browser pop-up notifications"
        hint={browserBlocked
          ? "Blocked by your browser. Enable HomeIQa notifications in browser settings, then refresh."
          : "Show OS-level notifications when the HomeIQa tab isn't visible."}
        checked={prefs.browserNotify && browserGranted}
        onChange={v => {
          if (v && !browserGranted) { enableBrowser(); return; }
          update({ browserNotify: v });
        }}
        disabled={browserBlocked}
      />
      <ToggleRow
        label={pushBusy ? 'Lock-screen notifications · working…' : 'Lock-screen notifications'}
        hint={
          pushError
            ? `⚠ ${pushError}`
            : pushOn
              ? "On. You'll get pushes on this device even when the HomeIQa tab is closed."
              : "Off. Turn on to receive new chat + email pushes when the browser is closed. On iPhone, install HomeIQa to your home screen first."
        }
        checked={pushOn}
        onChange={(v) => togglePush(v)}
        disabled={pushBusy || browserBlocked}
      />

      <div style={{ display: 'flex', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
        <Btn variant="ghost" onClick={() => playChime(null)}>🔊 Test sound</Btn>
        {!browserGranted && !browserBlocked && (
          <Btn variant="gold" onClick={enableBrowser}>Enable browser notifications</Btn>
        )}
        {browserGranted && (
          <span style={{ alignSelf: 'center', fontSize: 11, color: '#0F6B3F', fontWeight: 500, letterSpacing: '0.04em' }}>
            ✓ Browser notifications allowed
          </span>
        )}
      </div>
    </Card>
  );
}

function ToggleRow({ label, hint, checked, onChange, disabled }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
      gap: 16, padding: '14px 0', borderTop: `1px solid ${BORDER}`,
      opacity: disabled ? 0.6 : 1
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: BURG_DK, marginBottom: 3 }}>{label}</div>
        <div style={{ fontSize: 11.5, color: MID, lineHeight: 1.5 }}>{hint}</div>
      </div>
      <button
        type="button"
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        aria-pressed={checked}
        style={{
          flexShrink: 0,
          width: 44, height: 24, borderRadius: 999,
          background: checked ? BURG : '#D5D5D5',
          border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
          position: 'relative', padding: 0, marginTop: 2,
          transition: 'background 0.15s ease'
        }}
      >
        <span style={{
          position: 'absolute',
          top: 2, left: checked ? 22 : 2,
          width: 20, height: 20, borderRadius: '50%',
          background: checked ? GOLD : '#fff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          transition: 'left 0.15s ease, background 0.15s ease'
        }} />
      </button>
    </div>
  );
}

function IntroSmsCard() {
  return (
    <Card title="Intro SMS to new leads" right={<Pill color="gold">Always reviewed</Pill>}>
      <div style={{ fontSize: 13, color: MID, lineHeight: 1.6, marginBottom: 14 }}>
        Intro texts are <strong>never sent automatically</strong>. Whenever you add a new lead with a phone
        number, a confirm popup asks if you want to text the seller. You pick one of the messages below
        (or write your own), edit it, and confirm the <strong>city &amp; state</strong> are included — then
        press Send. The text only goes out once, from your business SMS number.
      </div>
      <Field label="Available intro messages">
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: BURG_DK, lineHeight: 1.6 }}>
          {INTRO_SMS_OPTIONS.map(opt => (
            <li key={opt.id} style={{ marginBottom: 8 }}>
              <strong style={{ color: BURG }}>{opt.label}</strong>
              <div style={{ color: MID, fontSize: 12.5 }}>{opt.body}</div>
            </li>
          ))}
          <li><strong style={{ color: BURG }}>Custom</strong> <span style={{ color: MID, fontSize: 12.5 }}>— type your own message in the popup.</span></li>
        </ul>
      </Field>
      <div style={{ fontSize: 11.5, color: LIGHT, lineHeight: 1.55, marginTop: 4 }}>
        <code>{'{agentName}'}</code> fills in your first name; <code>{'{city}'}</code> / <code>{'{state}'}</code> fill in
        from the lead's address when known (otherwise you'll be prompted to type them in).
      </div>
    </Card>
  );
}

function InviteCard({ adminUid, adminEmail }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('agent'); // 'agent' or 'lead_submitter'
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setMsg('');
    if (!email.trim()) return;
    setSending(true);
    try {
      const fn = httpsCallable(functions, 'sendAgentInvite');
      const res = await fn({ email: email.trim(), name: name.trim(), role });
      setMsg(res.data?.message || `✓ Invite recorded for ${email.trim()}.`);
      setEmail(''); setName(''); setRole('agent');
    } catch (e) {
      try {
        const key = email.trim().toLowerCase();
        await setDoc(doc(db, 'agentInvites', key), {
          email: key, name: name.trim(),
          role,
          invitedBy: adminEmail,
          invitedByUid: adminUid,
          createdAt: serverTimestamp(),
          status: 'pending'
        });
        setMsg(`✓ Invite recorded for ${key} (email send unavailable: ${e.message})`);
      } catch (e2) {
        setMsg(`Error: ${e2.message}`);
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Card title="Invite a new agent" right={<Pill color="gold">Admin only</Pill>}>
      <form onSubmit={submit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.2fr 1fr 140px', gap: 12, alignItems: 'end' }}>
          <Field label="Email" required>
            <Input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="agent@example.com" />
          </Field>
          <Field label="Name (optional)"><Input value={name} onChange={e => setName(e.target.value)} placeholder="Jane Doe" /></Field>
          <Field label="Role" required>
            <select
              value={role}
              onChange={e => setRole(e.target.value)}
              style={{ padding: '10px 12px', border: '1px solid rgba(107,30,46,0.1)', borderRadius: 4, fontSize: 13, fontFamily: "'DM Sans', sans-serif", background: '#fff' }}
            >
              <option value="agent">Contract Agent — full CRM access · 25% to HomeIQa by default</option>
              <option value="lead_submitter">Lead Submitter — submit leads only</option>
            </select>
          </Field>
          <Btn type="submit" disabled={sending}>{sending ? 'Inviting…' : 'Send invite'}</Btn>
        </div>
        <div style={{ fontSize: 11, color: LIGHT, marginTop: 4, lineHeight: 1.55 }}>
          <strong style={{ color: MID }}>Contract Agent</strong> — full pipeline, calculator, contracts, SMS, settings. HomeIQa retains 25% of every assignment fee by default; the rest is the agent's payout. Override per deal on the lead.
          {' '}
          <strong style={{ color: MID }}>Lead Submitter</strong> — only sees a submit-a-lead form and a list of their own submissions. Their leads land in your pipeline tagged with their name.
        </div>
        {msg && <div style={{ fontSize: 12, color: BURG, marginTop: 10 }}>{msg}</div>}
      </form>
    </Card>
  );
}

function AgentList({ adminEmail }) {
  const [agents, setAgents] = useState([]);
  const [invites, setInvites] = useState([]);

  useEffect(() => {
    const u1 = onSnapshot(query(collection(db, 'agents')), snap =>
      setAgents(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
    const u2 = onSnapshot(query(collection(db, 'agentInvites')), snap =>
      setInvites(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
    return () => { u1(); u2(); };
  }, []);

  const [pendingRevoke, setPendingRevoke] = useState(null);
  const revoke = (a) => setPendingRevoke(a);
  const confirmRevoke = async () => {
    if (!pendingRevoke) return;
    await updateDoc(doc(db, 'agents', pendingRevoke.id), { status: 'revoked' });
    setPendingRevoke(null);
  };

  const reactivate = async (a) => {
    await updateDoc(doc(db, 'agents', a.id), { status: 'active' });
  };

  const cancelInvite = async (id) => {
    await deleteDoc(doc(db, 'agentInvites', id));
  };

  // Update the per-agent default commission split. Stored as a fraction
  // 0..1 (so 25% = 0.25). Admin can edit any contract agent's split.
  const updateSplit = async (a, pctString) => {
    const pct = Math.max(0, Math.min(100, Number(pctString) || 0)) / 100;
    await updateDoc(doc(db, 'agents', a.id), { defaultCommissionToHomeIQa: pct });
  };

  const friendlyRole = (a) => {
    if (a.role === 'admin') return 'Owner';
    if (a.role === 'lead_submitter') return 'Lead Submitter';
    if (a.isPlaceholder) return 'Lead Agent (link only)';
    return 'Contract Agent';
  };

  const copyLink = async (a) => {
    if (!a.intakeSlug) return;
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://homeiqa.com';
    try { await navigator.clipboard.writeText(`${origin}/lead-intake/${a.intakeSlug}`); } catch {}
  };

  const removePlaceholder = async (a) => {
    if (!a.isPlaceholder) return;
    await deleteDoc(doc(db, 'agents', a.id));
  };

  return (
    <>
      <Card title={`Active team (${agents.length})`} padded={false}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Email</th>
              <th style={s.th}>Name</th>
              <th style={s.th}>Role</th>
              <th style={s.th}>Default split</th>
              <th style={s.th}>Status</th>
              <th style={s.th}></th>
            </tr>
          </thead>
          <tbody>
            {agents.map(a => (
              <tr key={a.id}>
                <td style={s.td}>{a.email || (a.isPlaceholder ? <em style={{ color: LIGHT }}>no email</em> : '—')}</td>
                <td style={s.td}>{a.name || '—'}</td>
                <td style={s.td}>
                  <Pill color={a.role === 'admin' ? 'gold' : a.role === 'lead_submitter' ? 'gray' : a.isPlaceholder ? 'gold' : 'burg'}>{friendlyRole(a)}</Pill>
                </td>
                <td style={s.td}>
                  {a.role === 'agent' ? (
                    <SplitInput
                      value={typeof a.defaultCommissionToHomeIQa === 'number' ? Math.round(a.defaultCommissionToHomeIQa * 100) : 25}
                      onSave={(v) => updateSplit(a, v)}
                    />
                  ) : '—'}
                </td>
                <td style={s.td}><Pill color={a.status === 'active' ? 'green' : a.status === 'revoked' ? 'red' : 'gray'}>{a.status}</Pill></td>
                <td style={s.td}>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {a.intakeSlug && (
                      <button onClick={() => copyLink(a)} style={s.linkBtn} title={`Share link: /lead-intake/${a.intakeSlug}`}>🔗 Copy link</button>
                    )}
                    {a.isPlaceholder ? (
                      <button onClick={() => removePlaceholder(a)} style={{ ...s.linkBtn, color: '#991B1B' }}>Remove</button>
                    ) : a.role !== 'admin' && (
                      a.status === 'revoked'
                        ? <button onClick={() => reactivate(a)} style={s.linkBtn}>Reactivate</button>
                        : <button onClick={() => revoke(a)} style={{ ...s.linkBtn, color: '#991B1B' }}>Revoke</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {pendingRevoke && (
        <div style={revokeStyles.backdrop} onClick={() => setPendingRevoke(null)}>
          <div style={revokeStyles.panel} onClick={e => e.stopPropagation()}>
            <div style={revokeStyles.title}>Revoke access for {pendingRevoke.email}?</div>
            <div style={revokeStyles.body}>
              They'll be locked out immediately. You can reactivate them at any time from this same list.
            </div>
            <div style={revokeStyles.actions}>
              <Btn variant="ghost" onClick={() => setPendingRevoke(null)}>Cancel</Btn>
              <Btn variant="danger" onClick={confirmRevoke}>Revoke access</Btn>
            </div>
          </div>
        </div>
      )}

      {invites.length > 0 && (
        <Card title={`Pending invites (${invites.length})`} padded={false}>
          <table style={s.table}>
            <thead><tr><th style={s.th}>Email</th><th style={s.th}>Name</th><th style={s.th}>Invited by</th><th style={s.th}></th></tr></thead>
            <tbody>
              {invites.map(i => (
                <tr key={i.id}>
                  <td style={s.td}>{i.email}</td>
                  <td style={s.td}>{i.name || '—'}</td>
                  <td style={s.td}>{i.invitedBy}</td>
                  <td style={s.td}><button onClick={() => cancelInvite(i.id)} style={{ ...s.linkBtn, color: '#991B1B' }}>Cancel</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}

const s = {
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '12px 18px', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: LIGHT, borderBottom: `1px solid ${BORDER}` },
  td: { padding: '12px 18px', fontSize: 13, borderBottom: `1px solid ${BORDER}`, color: '#1C1C1C' },
  linkBtn: { background: 'transparent', border: 'none', color: BURG, fontSize: 11, cursor: 'pointer', padding: 0, fontFamily: "'DM Sans', sans-serif" }
};

// Inline editor for an agent's default commission split. Shows "25%" with
// a small ✎ button; click to edit, type a new percent, blur or enter to save.
function SplitInput({ value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value);
  useEffect(() => { setV(value); }, [value]);

  const save = async () => {
    setEditing(false);
    if (Number(v) !== Number(value)) { await onSave(v); }
  };

  if (!editing) {
    return (
      <button onClick={() => setEditing(true)} style={splitS.pill} title="Click to change">
        {value}% to HomeIQa <span style={splitS.pencil}>✎</span>
      </button>
    );
  }
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <input
        type="number"
        autoFocus
        min="0"
        max="100"
        value={v}
        onChange={e => setV(e.target.value)}
        onBlur={save}
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setEditing(false); setV(value); } }}
        style={splitS.input}
      />
      <span style={{ fontSize: 11, color: MID }}>%</span>
    </div>
  );
}

const splitS = {
  pill: { background: GOLD, color: BURG_DK, border: 'none', borderRadius: 999, padding: '4px 10px', fontSize: 11, fontWeight: 500, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", display: 'inline-flex', alignItems: 'center', gap: 4 },
  pencil: { fontSize: 9, opacity: 0.7 },
  input: { width: 50, padding: '4px 6px', border: `1px solid ${BORDER}`, borderRadius: 4, fontSize: 12, fontFamily: "'DM Sans', sans-serif", textAlign: 'right' }
};

const revokeStyles = {
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(28,28,28,0.65)', zIndex: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  panel: { background: '#fff', borderRadius: 14, maxWidth: 420, width: '100%', padding: '24px 26px', boxShadow: '0 24px 64px rgba(0,0,0,0.4)', fontFamily: "'DM Sans', sans-serif" },
  title: { fontFamily: "'Cormorant Garamond', serif", fontSize: 20, fontWeight: 500, color: BURG_DK, marginBottom: 10 },
  body: { fontSize: 13, color: MID, lineHeight: 1.6, marginBottom: 18 },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 10 }
};
